import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TruckIcon, CheckCircle2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FIFO Batch-receive modal.
 * props: { open, onClose, vendorName, entries (from pending_vendor_receipts filtered by vendor), onDone }
 * entries share vendor_id but may have multiple (part_id, stage) rows.
 */
export default function BatchReceiveDialog({ open, onClose, vendorName, entries, onDone }) {
  const grouped = (entries || []).reduce((acc, e) => {
    const key = `${e.part_id}|${e.stage}`;
    if (!acc[key]) acc[key] = { part_id: e.part_id, part_name: e.part_name, stage: e.stage, vendor_id: e.vendor_id, pending_qty: 0 };
    acc[key].pending_qty += e.pending_qty || 0;
    return acc;
  }, {});
  const groupList = Object.values(grouped);

  const [selectedKey, setSelectedKey] = useState('');
  const [qty, setQty] = useState('');
  const [recvDate, setRecvDate] = useState(new Date().toISOString().split('T')[0]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-select first group when opening
  useEffect(() => {
    if (open && groupList.length > 0 && !selectedKey) {
      setSelectedKey(`${groupList[0].part_id}|${groupList[0].stage}`);
    }
    if (!open) {
      setSelectedKey(''); setQty(''); setPreview(null);
    }
  }, [open, groupList, selectedKey]);

  const selected = grouped[selectedKey];

  const fetchPreview = useCallback(async () => {
    if (!selected || !qty || parseInt(qty) <= 0) { setPreview(null); return; }
    setPreviewing(true);
    try {
      const res = await axios.get(`${API}/challans/batch-receive-preview`, {
        params: {
          part_id: selected.part_id, vendor_id: selected.vendor_id,
          stage_to: selected.stage, qty: parseInt(qty)
        }
      });
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'Failed to preview allocation');
    } finally { setPreviewing(false); }
  }, [selected, qty]);

  // Debounce preview when qty changes
  useEffect(() => {
    if (!qty || !selected) { setPreview(null); return; }
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [qty, selected, fetchPreview]);

  const handleSubmit = async () => {
    if (!selected || !qty || parseInt(qty) <= 0) { toast.error('Enter a valid quantity'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/challans/batch-receive`, {
        part_id: selected.part_id, vendor_id: selected.vendor_id, stage_to: selected.stage,
        qty: parseInt(qty), date: new Date(recvDate).toISOString(),
      });
      toast.success(res.data.message);
      onDone?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to receive'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="batch-receive-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            <TruckIcon className="w-6 h-6 inline mr-2 text-indigo-700" />
            RECEIVE BATCH — {vendorName}
          </DialogTitle>
          <p className="text-xs text-text-muted">Quantity will be FIFO-allocated across pending challans (oldest first). Each split becomes its own stage receipt.</p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part / Stage</Label>
            <select
              value={selectedKey}
              onChange={(e) => { setSelectedKey(e.target.value); setPreview(null); }}
              className="mt-1.5 w-full rounded-none border border-gray-300 bg-white h-10 px-2 text-sm"
              data-testid="batch-receive-part-select"
            >
              {groupList.map(g => {
                const key = `${g.part_id}|${g.stage}`;
                return (
                  <option key={key} value={key}>
                    {`${g.part_id} — ${g.stage} (pending: ${g.pending_qty})`}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Received Date</Label>
            <Input type="date" value={recvDate} onChange={(e) => setRecvDate(e.target.value)} className="mt-1.5 rounded-none" data-testid="batch-receive-date" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity to Receive</Label>
            <Input
              type="number" min="1" value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder={selected ? `Max recommended: ${selected.pending_qty}` : ''}
              className="mt-1.5 rounded-none font-mono text-lg"
              data-testid="batch-receive-qty"
            />
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="mt-4 border border-slate-200 rounded-sm" data-testid="batch-receive-preview">
            <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">FIFO Allocation Preview</span>
              <div className="flex items-center gap-3 text-xs">
                <span>Requested: <span className="font-mono font-semibold">{preview.requested_qty}</span></span>
                <span>Total Pending: <span className="font-mono">{preview.total_pending_across_challans}</span></span>
                {preview.unallocated_qty > 0 ? (
                  <span className="text-red-700 font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />Unallocated: {preview.unallocated_qty}
                  </span>
                ) : (
                  <span className="text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />Fully allocated
                  </span>
                )}
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Challan #</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Challan Qty</th>
                  <th className="text-right px-3 py-2">Already Recv</th>
                  <th className="text-right px-3 py-2">Pending</th>
                  <th className="text-right px-3 py-2">This Receipt</th>
                  <th className="text-right px-3 py-2">After</th>
                </tr>
              </thead>
              <tbody>
                {preview.allocations.map((a, i) => (
                  <tr key={a.challan_id} className="border-t border-slate-100" data-testid={`preview-alloc-row-${i}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{a.challan_no}</td>
                    <td className="px-3 py-2 text-text-muted">{(a.challan_date || '').split('T')[0]}</td>
                    <td className="px-3 py-2 text-right font-mono">{a.challan_qty}</td>
                    <td className="px-3 py-2 text-right font-mono text-text-muted">{a.already_received}</td>
                    <td className="px-3 py-2 text-right font-mono">{a.pending_before}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">+{a.allocated}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {a.pending_after === 0 ? (
                        <span className="text-emerald-700 font-semibold">✓ complete</span>
                      ) : (
                        <span>{a.pending_after} left</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {previewing && <div className="text-xs text-text-muted mt-2">Calculating allocation…</div>}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="batch-receive-cancel">CANCEL</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !preview || preview.allocations.every(a => a.allocated === 0)}
            className="bg-indigo-700 text-white hover:bg-indigo-800 rounded-sm"
            data-testid="batch-receive-submit"
          >
            {submitting ? 'RECEIVING…' : 'CONFIRM RECEIVE'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
